/**
 * In-app webview detection from a user-agent string.
 *
 * An "in-app webview" is the embedded browser a native app (Facebook,
 * Instagram, Messenger, …) opens when a user taps a link inside the app,
 * instead of handing off to the system browser.
 *
 * Why the fetch layer cares (see `request-timeout.ts` / `use-fetch-base.ts`):
 * these webviews — especially Facebook and Instagram on iOS — throttle the JS
 * main thread and the network far more aggressively than a standalone browser.
 * A request that finishes in ~2s in Mobile Safari can take 10-20s in the same
 * device's FB/IG webview. A single fixed timeout therefore aborts legitimate,
 * still-in-flight requests (including the non-idempotent checkout charge),
 * turning a slow-but-successful call into an ambiguous client-side failure that
 * pushes the user into a risky manual retry. We widen the timeout budget when
 * we detect one of these environments.
 *
 * Pure (the UA string is passed in) so it is unit-testable without mocking the
 * non-configurable `navigator.userAgent` global (see CLAUDE.md → jsdom v30
 * mocking constraints).
 */

export type InAppWebview = "facebook" | "instagram" | "messenger";

// Order matters: the most specific signature wins. Messenger and Instagram are
// tested before the generic Facebook tokens because their UAs also carry the
// shared `FBAN`/`FBAV` markers, and we want to attribute the precise source.
const IN_APP_WEBVIEW_SIGNATURES: ReadonlyArray<{
    name: InAppWebview;
    pattern: RegExp;
}> = [
    // Instagram in-app browser carries the literal "Instagram" token.
    { name: "instagram", pattern: /Instagram/i },
    // Messenger (iOS `FBAN/MessengerForiOS`, Android `Orca-Android`).
    { name: "messenger", pattern: /MessengerForiOS|Orca-Android/i },
    // Facebook app: iOS `FBAN/FBIOS;FBAV/…`, Android `FB_IAB/FB4A`.
    { name: "facebook", pattern: /FBAN|FBAV|FB_IAB|FB4A|FBIOS/i },
];

/**
 * Returns which in-app webview the user-agent belongs to, or `null` for a
 * standalone browser. Exposed (not just the boolean) so callers can attach the
 * source to observability without re-parsing the UA.
 */
export function getInAppWebview(
    userAgent: string | null | undefined,
): InAppWebview | null {
    if (!userAgent) return null;

    for (const { name, pattern } of IN_APP_WEBVIEW_SIGNATURES) {
        if (pattern.test(userAgent)) return name;
    }

    return null;
}

/** Whether the user-agent belongs to a known in-app webview. */
export function isInAppWebview(userAgent: string | null | undefined): boolean {
    return getInAppWebview(userAgent) !== null;
}
